// Review-mark keys. The engine's keys (docs/design.md "Change keys" and
// "Partial review marks") are what review marks are stored under. The
// TypeScript fallback cannot reproduce them exactly (its diff can split
// hunks differently from the engine's histogram diff), so its keys carry
// FALLBACK_PREFIX and are never stored as marks.

export const FALLBACK_PREFIX = 'fallback:';

/*
 * The engine's key grammar (crates/engine/src/marks.rs `Key::parse` reads
 * the same):
 *
 *   key    = hex16 | range
 *   range  = anchor "." at [ "." anchor "." at ] "." fp "." count
 *   hex16  = 16 lowercase hex digits      change, block and row keys
 *   anchor = hex16                        the window's least row hash, then
 *                                         its second-least distinct one
 *   fp     = hex16                        the window's fingerprint
 *   count  = 1-9 digits, no leading zero  window length, from 1
 *   at     = "0", or 1-9 digits, no leading zero; the anchor's first
 *            offset in the window, below count
 *
 * A second anchor is greater than the first (as hex16 strings, which order
 * as their numbers do) and at another offset; a lone anchor (a window of one
 * repeated row) is at 0. A range is a partial mark (`EngineApi.rangeKeys`).
 */
const HEX16 = '[0-9a-f]{16}';
const COUNT = '[1-9][0-9]{0,8}';
const INDEX = `(?:0|${COUNT})`;
const PLAIN = new RegExp(`^${HEX16}$`);
const RANGE = new RegExp(`^(${HEX16})\\.(${INDEX})(?:\\.(${HEX16})\\.(${INDEX}))?\\.(${HEX16})\\.(${COUNT})$`);

/** A row hash at its first offset in a window. */
export type Anchor = { hash: string; at: number };

/** A parsed range key. */
export type RangeKey = { first: Anchor; second: Anchor | null; fp: string; count: number };

/** The parts of a range key under `prefix` ('' for the engine's), or null
 *  when `key` is not one. */
export function parseRangeKey(key: string, prefix = ''): RangeKey | null {
  if (!key.startsWith(prefix)) return null;
  const m = RANGE.exec(key.slice(prefix.length));
  if (!m) return null;
  const count = Number(m[6]);
  const first = { hash: m[1], at: Number(m[2]) };
  const second = m[3] === undefined ? null : { hash: m[3], at: Number(m[4]) };
  const ok =
    first.at < count && (second === null ? first.at === 0 : second.at < count && second.at !== first.at && second.hash > first.hash);
  return ok ? { first, second, fp: m[5], count } : null;
}

/** A range key under `prefix`, the inverse of `parseRangeKey`. */
export function formatRangeKey(k: RangeKey, prefix = ''): string {
  const anchors = [k.first, ...(k.second ? [k.second] : [])].map((a) => `${a.hash}.${a.at}`);
  return `${prefix}${anchors.join('.')}.${k.fp}.${k.count}`;
}

/** True for 16 lowercase hex digits (a row hash, or a change or block key). */
export function isHex16(s: string): boolean {
  return PLAIN.test(s);
}

/** True for a key the Wasm engine produced (the only keys marks may use). */
export function isEngineKey(key: string): boolean {
  return isHex16(key) || parseRangeKey(key) !== null;
}
